import type { ReactNode } from "react";
import { useI18n } from "@expertloop/i18n/react";
import type { AdminSection } from "./useAdminSection";

interface SectionShellProps<T> {
	section: AdminSection<T>;
	children: (data: T) => ReactNode;
}

/**
 * Cadre commun d'un bloc de la vue d'ensemble.
 *
 * Il rend l'un des quatre états possibles et n'en invente aucun cinquième :
 * tant que la donnée n'est pas arrivée, aucune valeur n'est affichée à sa
 * place, et un zéro n'est jamais substitué à une absence.
 */
export function SectionShell<T>({ section, children }: SectionShellProps<T>) {
	const { t } = useI18n();
	const { state } = section;

	if (state.status === "loading") {
		return <p className="admin-overview__state">{t("adminOverview.loading")}</p>;
	}

	if (state.status === "forbidden") {
		return (
			<p className="admin-overview__state admin-overview__state--muted">
				{t("adminOverview.forbidden")}
			</p>
		);
	}

	if (state.status === "failed") {
		return (
			<p className="admin-overview__state admin-overview__state--error">
				{t("adminOverview.loadFailed")}{" "}
				<button type="button" className="admin-overview__retry" onClick={section.reload}>
					{t("adminOverview.retry")}
				</button>
			</p>
		);
	}

	return <>{children(state.data)}</>;
}
